// Benchmark grading + KPI math for the Marketing Dashboard Suite.
// Grading legend (from Alex's Settings tab):
//   🟢 green  — at or above benchmark (on track)
//   🟡 yellow — within 20% below benchmark (watch closely)
//   🔴 red    — more than 20% below benchmark (needs attention)
// For "lower is better" benchmarks (CPL, cost-per-purchase, bounce ceilings)
// the comparison is inverted: at/below target is green.

export type Grade = "green" | "yellow" | "red" | "none";

export function gradeMetric(
  actual: number | null | undefined,
  target: number,
  direction: "higher" | "lower",
): Grade {
  if (actual === null || actual === undefined || !Number.isFinite(actual)) return "none";
  if (!Number.isFinite(target) || target === 0) return "none";

  if (direction === "higher") {
    if (actual >= target) return "green";
    if (actual >= target * 0.8) return "yellow";
    return "red";
  }
  // lower is better
  if (actual <= target) return "green";
  if (actual <= target * 1.2) return "yellow";
  return "red";
}

// Tailwind text colors matching the rest of the app (emerald / amber / red).
export const GRADE_TEXT: Record<Grade, string> = {
  green: "text-emerald-600",
  yellow: "text-amber-600",
  red: "text-red-600",
  none: "text-muted-foreground",
};

// ── KPI computations (from raw volume totals) ──────────────────────
// All ratios returned as fractions (0.032 = 3.2%). Denominator guards return
// null so the UI can render "—" instead of a divide-by-zero.

const safeDiv = (num: number, den: number): number | null => (den > 0 ? num / den : null);

/** Engagement Rate = (likes + comments + saves + shares) ÷ reach */
export function engagementRate(v: {
  likes?: number;
  comments?: number;
  saves?: number;
  shares?: number;
  reach?: number;
}): number | null {
  const interactions = (v.likes ?? 0) + (v.comments ?? 0) + (v.saves ?? 0) + (v.shares ?? 0);
  return safeDiv(interactions, v.reach ?? 0);
}

/** Save Rate = saves ÷ reach */
export const saveRate = (saves: number, reach: number) => safeDiv(saves, reach);

/** Link CTR = link clicks ÷ reach */
export const linkCtr = (linkClicks: number, reach: number) => safeDiv(linkClicks, reach);

/** Month-over-month growth = (current − prior) ÷ prior */
export function momGrowth(current: number, prior: number): number | null {
  return prior > 0 ? (current - prior) / prior : null;
}

/** Close Rate = deals closed ÷ leads */
export const closeRate = (deals: number, leads: number) => safeDiv(deals, leads);

// ── Paid-ads KPIs ──────────────────────────────────────────────────
/** Cost Per Lead = ad spend ÷ leads */
export const costPerLead = (spend: number, leads: number) => safeDiv(spend, leads);
/** Cost Per Purchase = ad spend ÷ purchases */
export const costPerPurchase = (spend: number, purchases: number) => safeDiv(spend, purchases);
/** ROAS = revenue ÷ ad spend */
export const roas = (revenue: number, spend: number) => safeDiv(revenue, spend);
/** Cost Per Click = ad spend ÷ link clicks */
export const costPerClick = (spend: number, clicks: number) => safeDiv(spend, clicks);
/** Click-through rate = link clicks ÷ impressions */
export const paidCtr = (clicks: number, impressions: number) => safeDiv(clicks, impressions);
